import { useState } from 'react';
import { Modal, Tabs, TabList, Tab, TabPanel } from './ui';
import {Mail, Phone, Globe, User, MessageSquare, X} from 'lucide-react';
import { HelpModalProps } from '../types/components';
import { SupportForm } from './SupportForm';

export function HelpModal({ isOpen, onClose, clientData }: HelpModalProps) {
  const helpInfo = clientData?.helpInfo || {};
  const [activeTab, setActiveTab] = useState('contact');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Help & Contact" size="md">
        {/* Close button */}
        <button
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-6 right-6 rounded-full p-1 text-gray-500 hover:text-gray-700
                   bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
            <X className="h-6 w-auto" />
        </button>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabList>
            <Tab value="contact">Contact Info</Tab>
            <Tab value="support">Submit Request</Tab>
          </TabList>

          <TabPanel value="contact">
            <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Need assistance? We're here to help!
            </h3>
            <p className="text-gray-600 mb-6">
              Our support team is ready to answer your questions and help with any issues you may encounter.
            </p>
          </div>

          <div className="space-y-4">
            {helpInfo.email && (
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  <a
                    href={`mailto:${helpInfo.email}`}
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {helpInfo.email}
                  </a>
                </div>
              </div>
            )}

            {helpInfo.phone && (
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-600 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Phone</p>
                  <a
                    href={`tel:${helpInfo.phone.replace(/\D/g, '')}`}
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {helpInfo.phone}
                  </a>
                </div>
              </div>
            )}

            {helpInfo.website && (
              <div className="flex items-start space-x-3">
                <Globe className="w-5 h-5 text-blue-600 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Website</p>
                  <a
                    href={helpInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {helpInfo.website}
                  </a>
                </div>
              </div>
            )}

            {helpInfo.supportContact && (
              <div className="flex items-start space-x-3">
                <User className="w-5 h-5 text-blue-600 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Support Contact</p>
                  <p className="text-gray-600">{helpInfo.supportContact}</p>
                </div>
              </div>
            )}

            {helpInfo.provider && (
              <div className="flex items-start space-x-3">
                <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Provider</p>
                  <p className="text-gray-600">{helpInfo.provider}</p>
                </div>
              </div>
            )}

            {helpInfo.message && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-800">{helpInfo.message}</p>
              </div>
            )}
          </div>

              <div className="bg-blue-50 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Before contacting support, check your order history and account settings
                  to see if your question can be answered there.
                </p>
              </div>
            </div>
          </TabPanel>

          <TabPanel value="support">
            <SupportForm
              onSuccess={() => setActiveTab('contact')}
            />
          </TabPanel>
        </Tabs>
      </div>
    </Modal>
  );
}
